import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Spin } from 'antd';

import { COLORS } from '../utils/constants';
import { fluid } from '../utils/responsive';
import { getStaffPending, approveStaffRegistration, rejectStaffRegistration } from 'actions/userManagementV3';
import GridIcon from '../../../Assets/userManagementV3/gridIcon.svg';
import ListIcon from '../../../Assets/userManagementV3/listIcon.svg';
import { IMG_URL } from '../../../env.json';

const FILTER_CHIPS = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
];

const AVATAR_COLORS = ["#594099", "#1982C4", "#8AC926", "#F5A623", "#FF6B6B", "#4ECDC4", "#D97706", "#059669"];

const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
};

const resolveImage = (image) => {
    if (!image || image === "default.png") return "";
    return image.startsWith("http") ? image : `${IMG_URL}/${image}`;
};

const mapRecordToRow = (record) => ({
    id: record?.id,
    name: record?.name || "N/A",
    phone: record?.phone || record?.mobileNo || "",
    email: record?.email || "",
    image: resolveImage(record?.image),
    role: record?.role || record?.userRole || "Teacher",
    requestedAt: record?.createdAt,
    decidedAt: record?.updatedAt,
    approvalStatus: record?.approvalStatus || record?.status || "pending",
});

const MOCK_ROWS = [
    { id: "staff-req-1", name: "Pooja Sharma", phone: "9876543210", email: "pooja.sharma@example.com", image: "", role: "Teacher", requestedAt: "2026-08-25T11:00:00.000Z", decidedAt: null, approvalStatus: "pending" },
    { id: "staff-req-2", name: "Ramesh Verma", phone: "9812345678", email: "ramesh.v@example.com", image: "", role: "Operation", requestedAt: "2026-08-26T09:30:00.000Z", decidedAt: null, approvalStatus: "pending" },
    { id: "staff-req-3", name: "Sunita Patel", phone: "9845098450", email: "sunita.p@example.com", image: "", role: "Teacher", requestedAt: "2026-08-27T14:15:00.000Z", decidedAt: null, approvalStatus: "pending" },
    { id: "staff-req-4", name: "Vikram Malhotra", phone: "9900112233", email: "vikram.m@example.com", image: "", role: "Teacher", requestedAt: "2026-08-20T08:00:00.000Z", decidedAt: "2026-08-21T10:00:00.000Z", approvalStatus: "approved" },
    { id: "staff-req-5", name: "Deepa Krishnan", phone: "9871234560", email: "deepa.k@example.com", image: "", role: "Operation", requestedAt: "2026-08-19T13:00:00.000Z", decidedAt: "2026-08-20T16:00:00.000Z", approvalStatus: "approved" },
    { id: "staff-req-6", name: "Amitabh Sen", phone: "9823456789", email: "amitabh.s@example.com", image: "", role: "Teacher", requestedAt: "2026-08-18T10:00:00.000Z", decidedAt: "2026-08-19T12:00:00.000Z", approvalStatus: "rejected" },
];

const timeAgo = (value) => {
    if (!value) return "";
    const then = new Date(value).getTime();
    if (Number.isNaN(then)) return "";
    const diffMs = Date.now() - then;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const Avatar = React.memo(({ name, image, size = 52 }) => {
    const color = AVATAR_COLORS[(name || "N").charCodeAt(0) % AVATAR_COLORS.length];
    if (image) {
        return (
            <img
                src={image}
                alt={name}
                style={{ width: fluid(size), height: fluid(size), borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
        );
    }
    return (
        <div style={{
            width: fluid(size),
            height: fluid(size),
            borderRadius: "50%",
            backgroundColor: `${color}1A`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: fluid(size * 0.38),
            fontWeight: 700,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
Avatar.displayName = 'Avatar';

const ActionButtons = React.memo(({ row, filter, busy, onDecide }) => {
    if (filter === "approved") {
        return (
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "rejected")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#FF414D",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                CHANGE TO REJECT
            </button>
        );
    }
    if (filter === "rejected") {
        return (
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "approved")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#00A566",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                CHANGE TO APPROVE
            </button>
        );
    }
    return (
        <div style={{ display: "flex", alignItems: "center", gap: fluid(14) }}>
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "rejected")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#FF414D",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                REJECT
            </button>
            <button
                type="button"
                disabled={busy}
                onClick={() => onDecide(row.id, "approved")}
                style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: fluid(18),
                    fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                    color: "#00A566",
                    opacity: busy ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontFamily: "Roboto, sans-serif",
                }}
            >
                APPROVE
            </button>
        </div>
    );
});
ActionButtons.displayName = 'ActionButtons';

const COLUMN_TEMPLATE = `minmax(${fluid(260)}, 2fr) minmax(${fluid(140)}, 1.2fr) minmax(${fluid(140)}, 1.2fr) minmax(${fluid(220)}, 1.6fr)`;

const TableRow = React.memo(({ row, filter, busy, onDecide, isLast }) => (
    <div style={{ position: "relative" }}>
        <div style={{
            display: "grid",
            gridTemplateColumns: COLUMN_TEMPLATE,
            alignItems: "center",
            gap: fluid(16),
            minHeight: fluid(92),
            padding: `0 ${fluid(24)}`,
            boxSizing: "border-box",
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: fluid(14), minWidth: 0 }}>
                <Avatar name={row.name} image={row.image} size={52} />
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                        {row.name}
                    </span>
                    <span style={{ fontSize: "14px", lineHeight: "22px", fontWeight: 400, color: "#AEAEAE", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                        {row.phone ? row.phone.replace(/^\+91/, "") : row.email}
                    </span>
                </div>
            </div>

            <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#191919", fontFamily: "Roboto, sans-serif" }}>
                {String(row.role || "").toLowerCase().includes("operation") ? "Operations" : row.role}
            </span>

            <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#AEAEAE", fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap" }}>
                {timeAgo(filter === "pending" ? row.requestedAt : row.decidedAt)}
            </span>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%" }}>
                <ActionButtons row={row} filter={filter} busy={busy} onDecide={onDecide} />
            </div>
        </div>

        {!isLast && (
            <div style={{ position: "absolute", left: fluid(24), right: fluid(24), bottom: 0, height: "1px", backgroundColor: COLORS.borderLight }} />
        )}
    </div>
));
TableRow.displayName = 'TableRow';

const GridCard = React.memo(({ row, filter, busy, onDecide }) => (
    <div style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: fluid(22),
        boxSizing: "border-box",
        backgroundColor: "#FFFFFF",
        borderRadius: fluid(24),
        boxShadow: "4px 4px 10px 4px rgba(0,0,0,0.04)",
        gap: fluid(16),
    }}>
        <div style={{ display: "flex", alignItems: "center", gap: fluid(14), minWidth: 0 }}>
            <Avatar name={row.name} image={row.image} size={60} />
            <div style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: fluid(18), fontWeight: 600, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                    {row.name}
                </span>
                <span style={{ display: "block", fontSize: fluid(15), fontWeight: 400, color: "#555555", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif", marginTop: fluid(2) }}>
                    {(row.phone ? row.phone.replace(/^\+91/, "") : row.email)} · {String(row.role || "").toLowerCase().includes("operation") ? "Operations" : row.role}
                </span>
            </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: fluid(15), fontWeight: 400, color: "#8E8E8E", fontFamily: "Roboto, sans-serif" }}>
                {timeAgo(filter === "pending" ? row.requestedAt : row.decidedAt)}
            </span>
            <ActionButtons row={row} filter={filter} busy={busy} onDecide={onDecide} />
        </div>
    </div>
));
GridCard.displayName = 'GridCard';

const StaffRegistrationRequests = React.memo(({
    isMobile = false,
    onChanged,
    staffPending,
    getStaffPending,
    approveStaffRegistration,
    rejectStaffRegistration,
}) => {
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState("list");
    const [busyId, setBusyId] = useState(null);
    const [mockRows, setMockRows] = useState(MOCK_ROWS);

    const refetch = useCallback((status, callback) => {
        setLoading(true);
        if (getStaffPending) {
            getStaffPending({ status: status || filter }, () => {
                setLoading(false);
                callback?.();
            });
        }
        setTimeout(() => setLoading(false), 3000);
    }, [getStaffPending, filter]);

    useEffect(() => {
        refetch(filter);
    }, [filter, refetch]);

    const realRows = useMemo(() => {
        const list = Array.isArray(staffPending) ? staffPending : (staffPending?.items || staffPending?.data || []);
        return list.map(mapRecordToRow);
    }, [staffPending]);

    const usingMock = realRows.length === 0;
    const rows = useMemo(() => (
        usingMock ? mockRows.filter((row) => row.approvalStatus === filter) : realRows
    ), [usingMock, mockRows, realRows, filter]);

    const handleFilterChange = (value) => {
        if (value === filter) return;
        setFilter(value);
    };

    const handleDecide = useCallback((id, approvalStatus) => {
        if (usingMock) {
            setMockRows((prev) => prev.map((row) => (
                row.id === id ? { ...row, approvalStatus, decidedAt: new Date().toISOString() } : row
            )));
            onChanged?.();
            return;
        }

        setBusyId(id);
        const onDone = () => {
            setBusyId(null);
            refetch(filter, () => onChanged?.());
        };

        if (approvalStatus === "approved") {
            const row = rows.find((r) => r.id === id);
            approveStaffRegistration?.(id, { roles: [row?.role || "Teacher"], permissions: [] }, onDone);
        } else {
            rejectStaffRegistration?.(id, { reason: "Rejected by admin" }, onDone);
        }
    }, [usingMock, rows, approveStaffRegistration, rejectStaffRegistration, filter, refetch, onChanged]);

    return (
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: fluid(14),
                paddingBottom: fluid(18),
                flexShrink: 0,
            }}>
                <div style={{ display: "flex", gap: fluid(8) }}>
                    {FILTER_CHIPS.map((chip) => {
                        const isSelected = filter === chip.value;
                        return (
                            <button
                                type="button"
                                key={chip.value}
                                onClick={() => handleFilterChange(chip.value)}
                                style={{
                                    padding: `${fluid(6)} ${fluid(16)}`,
                                    borderRadius: fluid(20),
                                    fontSize: fluid(16),
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    border: `1px solid ${isSelected ? COLORS.primary : "#D9D9D9"}`,
                                    backgroundColor: isSelected ? COLORS.primary : "#FFF",
                                    color: isSelected ? "#FFF" : "#636363",
                                    transition: "all 0.2s",
                                }}
                            >
                                {chip.label}
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={() => setViewMode((m) => (m === "list" ? "grid" : "list"))}
                    title={viewMode === "list" ? "Grid view" : "List view"}
                    style={{
                        width: fluid(40),
                        height: fluid(40),
                        borderRadius: fluid(8),
                        border: "none",
                        backgroundColor: "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: fluid(4),
                        cursor: "pointer",
                        flexShrink: 0,
                    }}
                >
                    <img src={viewMode === "list" ? GridIcon : ListIcon} alt={viewMode === "list" ? "Grid view" : "List view"} style={{ width: fluid(28), height: fluid(28) }} />
                </button>
            </div>

            {!loading && usingMock && (
                <div style={{ fontSize: fluid(12), fontStyle: "italic", color: COLORS.textLight, paddingBottom: fluid(10), flexShrink: 0 }}>
                    Showing sample data — the server is unreachable right now, so this isn't the live list.
                </div>
            )}

            <style>{`.staff-reg-scroll::-webkit-scrollbar { display: none; }`}</style>

            <div style={{ flex: 1, minHeight: 0, position: "relative", display: "flex", flexDirection: "column" }}>
                {loading && (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#FFFFFF", zIndex: 10 }}>
                        <Spin size="large" />
                    </div>
                )}

                {viewMode === "list" && (
                    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", backgroundColor: "#FFFFFF", borderRadius: fluid(16), border: `1px solid ${COLORS.borderLight}`, boxSizing: "border-box", overflow: "hidden" }}>
                        {/* Sticky, non-scrolling header — only the rows below scroll. */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: COLUMN_TEMPLATE,
                            gap: fluid(16),
                            padding: `${fluid(16)} ${fluid(24)}`,
                            backgroundColor: "#F4F4F4",
                            flexShrink: 0,
                        }}>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Staff</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Role</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Requested</span>
                            <span style={{ fontSize: "15px", lineHeight: "24px", fontWeight: 500, color: "#A2A2A2", fontFamily: "Roboto, sans-serif" }}>Action</span>
                        </div>

                        <div
                            className="staff-reg-scroll"
                            style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", msOverflowStyle: "none" }}
                        >
                            {rows.length === 0 ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: fluid(200), color: COLORS.textLight, fontSize: fluid(14) }}>
                                    There are currently no {filter} registration requests.
                                </div>
                            ) : (
                                rows.map((row, index) => (
                                    <TableRow
                                        key={row.id}
                                        row={row}
                                        filter={filter}
                                        busy={busyId === row.id}
                                        onDecide={handleDecide}
                                        isLast={index === rows.length - 1}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                )}

                {viewMode === "grid" && (
                    <div
                        className="staff-reg-scroll"
                        style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                        {rows.length === 0 ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: fluid(200), color: COLORS.textLight, fontSize: fluid(14) }}>
                                There are currently no {filter} registration requests.
                            </div>
                        ) : (
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0, 1fr))",
                                gap: fluid(20),
                                paddingBottom: fluid(4),
                            }}>
                                {rows.map((row) => (
                                    <GridCard
                                        key={row.id}
                                        row={row}
                                        filter={filter}
                                        busy={busyId === row.id}
                                        onDecide={handleDecide}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

StaffRegistrationRequests.displayName = 'StaffRegistrationRequests';

const mapStateToProps = (state) => {
    const { staffPending } = state.userManagementV3;
    return { staffPending };
};

const mapDispatchToProps = (dispatch) => ({
    getStaffPending: (params, callback) => dispatch(getStaffPending(params, callback)),
    approveStaffRegistration: (reqId, params, callback) => dispatch(approveStaffRegistration(reqId, params, callback)),
    rejectStaffRegistration: (reqId, params, callback) => dispatch(rejectStaffRegistration(reqId, params, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StaffRegistrationRequests);
